import { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { AppShell, Group, Text, Stack, Avatar, Menu, ActionIcon, Tooltip, Center, Loader } from '@mantine/core';
import {
  IconPlane,
  IconDashboard,
  IconUpload,
  IconTable,
  IconCalendar,
  IconChartBar,
  IconBriefcase,
  IconUser,
  IconLogout,
  IconLayoutDashboard,
} from '@tabler/icons-react';

export default function DCLayout() {
  const { logout, user, getAccessTokenSilently } = useAuth0();
  const navigate = useNavigate();
  const location = useLocation();
  const [tokenReady, setTokenReady] = useState(false);

  const activePath = location.pathname;
  const userName = user?.name || 'Usuario';

  // Garantizar que el token esté en sessionStorage ANTES de renderizar
  // cualquier componente hijo que haga peticiones al API.
  useEffect(() => {
    // Si ya hay token guardado de una sesión activa, no esperar
    if (sessionStorage.getItem('token')) {
      setTokenReady(true);
      return;
    }
    const fetchToken = async () => {
      try {
        const token = await getAccessTokenSilently();
        sessionStorage.setItem('token', token);
        if (user?.nickname) sessionStorage.setItem('user', user.nickname);
      } catch (e) {
        console.error('[DCLayout] No se pudo obtener el token:', e.message);
      } finally {
        setTokenReady(true);
      }
    };
    fetchToken();
  }, [getAccessTokenSilently, user]);

  const handleLogout = () => {
    sessionStorage.removeItem('token');
    logout({ logoutParams: { returnTo: window.location.origin } });
  };

  return (
    <AppShell
      header={{ height: 64 }}
      navbar={{ width: 70, breakpoint: 'sm' }}
      padding="md"
    >
      {/* Header */}
      <AppShell.Header style={{ backgroundColor: '#020C41', borderBottom: '3px solid #FA0073' }}>
        <Group h="100%" px="md" justify="space-between">
          <Group gap="sm">
            <IconPlane size={28} color="white" />
            <Text fw={700} size="lg" c="white">Aeroméxico Cargo</Text>
          </Group>
          <Menu shadow="md" width={200}>
            <Menu.Target>
              <Avatar color="pink" radius="xl" style={{ cursor: 'pointer' }}>
                {userName
                  .split(' ')
                  .filter(Boolean)
                  .slice(0, 2)
                  .map((w) => w[0].toUpperCase())
                  .join('')}
              </Avatar>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Label>{userName}</Menu.Label>
              <Menu.Label>{user?.email}</Menu.Label>
              <Menu.Divider />
              <Menu.Item leftSection={<IconLogout size={14} />} onClick={handleLogout} color="red">
                Cerrar sesión
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </Group>
      </AppShell.Header>

      {/* Navbar — Solo iconos */}
      <AppShell.Navbar p="xs" style={{ backgroundColor: '#ffffff', borderRight: '1px solid #e0e0e0', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        <Stack gap="md" align="center" justify="center" style={{ flex: 1 }}>
          <Tooltip label="Dashboard" position="right" withArrow>
            <ActionIcon size="xl" variant={activePath === '/dc' ? 'light' : 'subtle'} color={activePath === '/dc' ? '#FA0073' : '#020C41'} onClick={() => navigate('/dc')}>
              <IconDashboard size={24} />
            </ActionIcon>
          </Tooltip>
          <Tooltip label="Carga de Archivos" position="right" withArrow>
            <ActionIcon size="xl" variant={activePath === '/dc/carga' ? 'light' : 'subtle'} color={activePath === '/dc/carga' ? '#FA0073' : '#020C41'} onClick={() => navigate('/dc/carga')}>
              <IconUpload size={24} />
            </ActionIcon>
          </Tooltip>
          <Tooltip label="CAP TD" position="right" withArrow>
            <ActionIcon size="xl" variant={activePath === '/dc/cap-td' ? 'light' : 'subtle'} color={activePath === '/dc/cap-td' ? '#FA0073' : '#020C41'} onClick={() => navigate('/dc/cap-td')}>
              <IconTable size={24} />
            </ActionIcon>
          </Tooltip>
          <Tooltip label="TD ITI" position="right" withArrow>
            <ActionIcon size="xl" variant={activePath === '/dc/td-iti' ? 'light' : 'subtle'} color={activePath === '/dc/td-iti' ? '#FA0073' : '#020C41'} onClick={() => navigate('/dc/td-iti')}>
              <IconCalendar size={24} />
            </ActionIcon>
          </Tooltip>
          <Tooltip label="FOC" position="right" withArrow>
            <ActionIcon size="xl" variant={activePath === '/dc/fco' ? 'light' : 'subtle'} color={activePath === '/dc/fco' ? '#FA0073' : '#020C41'} onClick={() => navigate('/dc/fco')}>
              <IconChartBar size={24} />
            </ActionIcon>
          </Tooltip>
          <Tooltip label="Consulta Comercial" position="right" withArrow>
            <ActionIcon size="xl" variant={activePath === '/dc/comercial' ? 'light' : 'subtle'} color={activePath === '/dc/comercial' ? '#FA0073' : '#020C41'} onClick={() => navigate('/dc/comercial')}>
              <IconBriefcase size={24} />
            </ActionIcon>
          </Tooltip>
        </Stack>
      </AppShell.Navbar>

      {/* Main Content */}
      <AppShell.Main style={{ backgroundColor: '#F2F2F0' }}>
        {tokenReady ? <Outlet /> : (
          <Center style={{ height: '100%' }}>
            <Loader color="#FA0073" type="dots" size={60} />
          </Center>
        )}
      </AppShell.Main>
    </AppShell>
  );
}
